import React, { Fragment, useState } from 'react';
import { get, map, merge } from 'lodash/fp';
import { Button, ButtonToolbar, Icon, Panel } from 'rsuite';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { GuestType } from '../../../../interfaces/guest';
import MainSettingsType from '../../../../interfaces/MainSettingsType';
import formatFullName from '../../../../helpers/formatFullName';
import toCustomFieldValue from '../../../../helpers/toCustomFieldValue';
import {
  getCustomFields,
  getPrimaryFields
} from '../../../../redux/selectors/client';
import { modifyAccess } from '../../../../helpers/permission';
import { MODULE as GUEST_MODULE } from '../../../../constants/permissions/guest';
import AccessControl from '../../../../components/AccessControl';
import RelatedGuests from '../../../../components/guest/RelatedGuests';
import {
  DetailsItem,
  ItemLabel,
  ItemValue
} from '../../../../components/guest/ViewElements';
import GuestForm from '../../../../components/guest/Form';

const PrimarySection = ({ guest, settings, ...props }) => {
  const { firstname, middlename, lastname } = guest;
  const isValidDob = moment(get('dob', guest)).isValid();
  const [editMode, setEditMode] = useState(false);
  const primaryFields = useSelector(getPrimaryFields);
  const customFields = useSelector(getCustomFields);
  const formFields = merge(primaryFields, customFields);

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const handleSaved = () => {
    setEditMode(false);
  };

  const isGuestRelationField = field =>
    get(`${field}.fieldType`, customFields) === 'guest_relation';

  return (
    <Panel {...props}>
      <ButtonToolbar style={{ textAlign: 'right' }}>
        {!editMode && (
          <AccessControl allowOnlyFor={modifyAccess(GUEST_MODULE)}>
            <Button appearance="primary" onClick={handleEdit}>
              <Icon icon="pencil" /> Edit
            </Button>
          </AccessControl>
        )}
      </ButtonToolbar>
      {editMode ? (
        <GuestForm
          data={guest}
          section="primary"
          formFields={formFields}
          onSaved={handleSaved}
          onCancel={handleCancel}
        />
      ) : (
        <Fragment>
          <DetailsItem>
            <ItemLabel>ID</ItemLabel>
            <ItemValue>{guest.id}</ItemValue>
          </DetailsItem>
          <DetailsItem>
            <ItemLabel>Name</ItemLabel>
            <ItemValue>
              {formatFullName([firstname, middlename, lastname])}
            </ItemValue>
          </DetailsItem>
          <DetailsItem>
            <ItemLabel>{get('dob.label', formFields)}</ItemLabel>
            {isValidDob && (
              <ItemValue>
                {moment(guest.dob).format('ll')}
                &nbsp; ( age: {moment().diff(guest.dob, 'years')} )
              </ItemValue>
            )}
          </DetailsItem>

          {settings.guestAdditionalGender === '1' && (
            <DetailsItem>
              <ItemLabel>{get('gender.label', formFields)}</ItemLabel>
              <ItemValue>
                {get(`gender.options.${guest.gender}`, formFields)}
              </ItemValue>
            </DetailsItem>
          )}

          {settings.guestAdditionalRace === '1' && (
            <DetailsItem>
              <ItemLabel>{get('race.label', formFields)}</ItemLabel>
              <ItemValue>
                {get(`race.options.${guest.race}`, formFields)}
              </ItemValue>
            </DetailsItem>
          )}

          {/* Dynamic Custom Fields */}
          {map(
            field => (
              <DetailsItem key={`cfField-${field.name}`}>
                <ItemLabel>{field.label}</ItemLabel>
                <ItemValue>
                  {toCustomFieldValue(guest[field.name], field)}
                  {isGuestRelationField(field.name) && (
                    <RelatedGuests
                      guestId={guest.id}
                      field={field.name}
                      showHeading
                    />
                  )}
                </ItemValue>
              </DetailsItem>
            ),
            customFields
          )}
        </Fragment>
      )}
    </Panel>
  );
};

PrimarySection.propTypes = {
  guest: GuestType,
  settings: MainSettingsType
};

PrimarySection.defaultProps = {
  guest: {},
  settings: {}
};

export default PrimarySection;
